import { Component } from "@angular/core";
import { CommonModule } from "@angular/common";

@Component({
    selector: "vsf-terms-conditions",
    standalone: true,
    imports: [CommonModule],
    template: `
        <div class="max-w-4xl mx-auto px-4 pt-8 pb-8 md:pt-6">
            <div class="prose max-w-none space-y-6 text-gray-700">
            <h1 class="text-3xl font-bold mb-6">Venduro B2B Platform Terms and Conditions</h1>
                <p class="text-sm text-gray-500">Effective date: 21 August 2026 Version: 1.2</p>
                <p>These Terms and Conditions (“Terms”) govern access to and use of the Venduro B2B wholesale platform available at <a href="https://venduro.io/" class="text-primary-600 hover:text-primary-800">venduro.io</a>, including the Buyer Portal, Seller Portal, product feeds, APIs and related services (collectively, the “Platform”).</p>
                <p>The Platform is operated by:</p>
                <p>
                    UAB Venduro<br />
                    Company code: 307175838<br />
                    VAT number: LT100018086112<br />
                    Partizanų g. 14-206<br />
                    LT-50211 Kaunas<br />
                    Lithuania
                </p>
                <p>(“Venduro”, “we”, “us” or “our”).</p>
                <p>The Platform is intended exclusively for business users and is not intended for consumers.</p>
                <p>By registering an account, accepting these Terms, submitting or accepting an Order, supplying Products to Venduro or otherwise using the Platform, the relevant business agrees to be bound by these Terms.</p>
                <h2 class="text-2xl font-semibold text-gray-900 pt-4">PART I – GENERAL TERMS</h2>
                <h3 class="text-xl font-semibold text-gray-900">1. Definitions</h3>
                <p>For the purposes of these Terms:</p>
                <p>“Buyer” means a business purchasing Products from Venduro.</p>
                <p>“Supplier” means a business supplying Products to Venduro.</p>
                <p>“Product” means any goods offered, ordered, supplied or purchased through or in connection with the Platform.</p>
                <p>“Buyer Order” means an order submitted by a Buyer to Venduro.</p>
                <p>“Purchase Order” or “PO” means an order submitted by Venduro to a Supplier.</p>
                <p>“Order” means, depending on the context, a Buyer Order, a Purchase Order or either of them.</p>
                <p>“Order Received” means an acknowledgement that Venduro has received a Buyer Order. It does not constitute acceptance of that Order by Venduro.</p>
                <p>“Order Confirmed” means confirmation by Venduro that a Buyer Order, or an identified part of it, has been accepted.</p>
                <p>“Business Day” means a day other than Saturday, Sunday or an official public holiday in Lithuania.</p>
                <p>“GTIN” includes GTIN, EAN, UPC or another equivalent unique product identifier accepted by Venduro.</p>
                <p>“Delivery”, for the purpose of calculating Buyer claim periods, means the date on which the relevant shipment is recorded as delivered by the carrier, proof-of-delivery system or other reliable delivery record, unless the Buyer demonstrates a different actual delivery date.</p>
                <h3 class="text-xl font-semibold text-gray-900">2. Business Use Only</h3>
                <p>2.1. Venduro is a business-to-business wholesale platform.</p>
                <p>2.2. Each user represents and warrants that:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>it is acting exclusively for business purposes;</li>
                    <li>it is validly established and legally entitled to conduct its business;</li>
                    <li>persons using its account are authorised to act on its behalf;</li>
                    <li>all information supplied to Venduro is complete, accurate and current; and</li>
                    <li>it has authority to enter into binding agreements with Venduro.</li>
                </ul>
                <p>2.3. Consumer rights and protections applicable exclusively to consumers do not apply.</p>
                <p>2.4. Venduro may request at any time:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>company registration documents;</li>
                    <li>VAT registration documents;</li>
                    <li>beneficial ownership information;</li>
                    <li>identification of authorised representatives;</li>
                    <li>bank account verification;</li>
                    <li>licences;</li>
                    <li>sourcing documentation;</li>
                    <li>financial information;</li>
                    <li>credit information;</li>
                    <li>compliance documentation; and</li>
                    <li>other information reasonably required for legal, commercial, fraud-prevention, credit-risk or compliance purposes.</li>
                </ul>
                <p>2.5. Venduro may independently verify information using public registers, VAT databases, credit-reference agencies, sanctions databases, fraud-prevention providers and other legitimate sources.</p>
                <p>2.6. Venduro may refuse registration, restrict functionality, impose additional verification requirements or terminate an account where Venduro reasonably considers the relationship to create unacceptable legal, credit, fraud, compliance, operational or commercial risk.</p>
                <h3 class="text-xl font-semibold text-gray-900">3. Commercial Structure</h3>
                <p>3.1. Unless expressly stated otherwise in an Order or separate written agreement:</p>
                <p>a. the Supplier sells Products to Venduro; and b. Venduro sells Products to the Buyer.</p>
                <p>3.2. The Supplier and Buyer do not enter into a direct contract of sale merely because Products supplied by the Supplier are ultimately delivered to that Buyer.</p>
                <p>3.3. Products may:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>be delivered directly to Venduro;</li>
                    <li>be delivered to a warehouse or logistics provider designated by Venduro;</li>
                    <li>be collected by a carrier arranged by Venduro;</li>
                    <li>be consolidated with Products supplied by other Suppliers; or</li>
                    <li>where instructed by Venduro, be shipped directly from a Supplier to a Buyer.</li>
                </ul>
                <p>3.4. The logistical route of the Products does not alter the contractual structure described in clause 3.1.</p>
                <p>3.5. Venduro may independently determine:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>resale prices;</li>
                    <li>Buyer discounts;</li>
                    <li>payment terms;</li>
                    <li>delivery charges;</li>
                    <li>promotional terms;</li>
                    <li>minimum order requirements; and</li>
                    <li>other Buyer-facing commercial conditions.</li>
                </ul>
                <p>3.6. Suppliers have no entitlement to information concerning Venduro’s Buyer pricing, margins, customer identity or other commercial terms, except to the extent that such information is reasonably necessary for fulfilment, delivery, compliance or another purpose expressly authorised by Venduro.</p>
                <p>3.7. Venduro may, at its sole discretion and without liability, correct, amend, withdraw or republish any Product listing, description, image, price or availability status at any time prior to dispatch, including after an Order has been confirmed, where Venduro reasonably identifies an error, inconsistency or outdated entry. Clause 33 (Prices) applies to pricing corrections specifically.</p>
                <h3 class="text-xl font-semibold text-gray-900">4. Acceptance of Terms and Electronic Evidence</h3>
                <p>4.1. Venduro may require electronic acceptance of these Terms.</p>
                <p>4.2. Venduro may record acceptance together with:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>account or company identifier;</li>
                    <li>user identifier;</li>
                    <li>date and time;</li>
                    <li>IP address;</li>
                    <li>applicable Terms version; and</li>
                    <li>other reasonable electronic evidence.</li>
                </ul>
                <p>4.3. The version of these Terms applicable when an Order is accepted forms part of that Order unless expressly agreed otherwise.</p>
                <p>4.4. Electronic Platform records, Orders, confirmations, emails, API records, timestamps and other electronic records may be relied upon as evidence of transactions and communications, and shall constitute conclusive evidence of their contents in the absence of manifest error.</p>
                <p>4.5. A user may not rely on its own standard terms, purchase conditions, sales conditions, invoice conditions or similar terms unless Venduro expressly accepts them in writing.</p>
                <h3 class="text-xl font-semibold text-gray-900">5. Order of Precedence</h3>
                <p>In the event of inconsistency between contractual documents, the following order of precedence applies:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>a separate written agreement expressly signed by Venduro;</li>
                    <li>the applicable Order Confirmation or Purchase Order;</li>
                    <li>specific written conditions expressly incorporated into that Order;</li>
                    <li>these Terms.</li>
                </ul>
                <p>No other terms override these Terms merely because they appear on an invoice, purchase order, delivery document, quotation, email footer, website or other document.</p>
                <h3 class="text-xl font-semibold text-gray-900">6. Account Security</h3>
                <p>6.1. Users are responsible for:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>maintaining the confidentiality of account credentials;</li>
                    <li>restricting account access to authorised persons;</li>
                    <li>activities conducted through their accounts; and</li>
                    <li>notifying Venduro immediately of suspected unauthorised access or misuse.</li>
                </ul>
                <p>6.2. Venduro may suspend or restrict account access without prior notice where reasonably necessary to protect the Platform, Venduro, Buyers, Suppliers or third parties.</p>
                <h2 class="text-2xl font-semibold text-gray-900 pt-4">PART II – SUPPLIER TERMS</h2>
                <h3 class="text-xl font-semibold text-gray-900">7. Supplier Relationship</h3>
                <p>7.1. A Supplier supplies Products to Venduro and not directly to Buyers unless Venduro expressly agrees otherwise.</p>
                <p>7.2. Venduro does not guarantee:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>minimum purchasing volumes;</li>
                    <li>minimum revenue;</li>
                    <li>minimum number of Purchase Orders;</li>
                    <li>continued listing of Products;</li>
                    <li>exclusivity;</li>
                    <li>minimum visibility; or</li>
                    <li>continued access to the Platform.</li>
                </ul>
                <p>7.3. Venduro may source identical or equivalent Products from other Suppliers at any time.</p>
                <p>7.4. Venduro may independently decide whether, when and in what quantities to purchase Products from a Supplier.</p>
                <h3 class="text-xl font-semibold text-gray-900">8. Purchase Orders</h3>
                <p>8.1. A Purchase Order becomes binding on the Supplier when accepted through:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>the Seller Portal;</li>
                    <li>API;</li>
                    <li>email;</li>
                    <li>written confirmation;</li>
                    <li>commencement of performance; or</li>
                    <li>another method accepted by Venduro.</li>
                </ul>
                <p>8.2. Once a PO has been accepted, the Supplier must fulfil it completely, accurately and within the agreed lead time.</p>
                <p>8.3. The Supplier may not change without Venduro’s prior approval:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>price;</li>
                    <li>GTIN;</li>
                    <li>Product;</li>
                    <li>quantity;</li>
                    <li>packaging configuration;</li>
                    <li>delivery date;</li>
                    <li>delivery location; or</li>
                    <li>any other material condition.</li>
                </ul>
                <p>8.4. Errors in the Supplier’s:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>stock feed;</li>
                    <li>inventory;</li>
                    <li>pricing;</li>
                    <li>catalogue;</li>
                    <li>API;</li>
                    <li>spreadsheet;</li>
                    <li>software;</li>
                    <li>integration;</li>
                    <li>internal system;</li>
                    <li>employee processing; or</li>
                    <li>service provider</li>
                </ul>
                <p>do not automatically release the Supplier from an accepted PO.</p>
                <p>8.5. Venduro may, at its discretion, permit correction of a genuine and obvious error.</p>
                <p>8.6. Repeated inaccurate stock information, incorrect prices, Order cancellations, late fulfilment or other performance failures may result in:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>reduced Product visibility;</li>
                    <li>suspension or removal of Products;</li>
                    <li>reduced purchasing activity;</li>
                    <li>additional confirmation or fulfilment requirements;</li>
                    <li>Supplier account restrictions;</li>
                    <li>temporary suspension; or</li>
                    <li>termination.</li>
                </ul>
                <h3 class="text-xl font-semibold text-gray-900">9. Product and Stock Information</h3>
                <p>9.1. The Supplier must provide complete, accurate and current information including, where applicable:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>GTIN;</li>
                    <li>Product name;</li>
                    <li>brand;</li>
                    <li>quantity;</li>
                    <li>purchase price;</li>
                    <li>selling unit;</li>
                    <li>minimum order requirements;</li>
                    <li>lead time;</li>
                    <li>expiry information;</li>
                    <li>shelf-life limitations;</li>
                    <li>country restrictions;</li>
                    <li>distribution restrictions;</li>
                    <li>channel restrictions;</li>
                    <li>transport restrictions; and</li>
                    <li>regulatory information.</li>
                </ul>
                <p>9.2. Stock information must be updated sufficiently frequently to remain reasonably reliable.</p>
                <p>9.3. The Supplier is responsible for information supplied directly by it or through its employees, agents, systems, files, APIs, integrations or service providers.</p>
                <p>9.4. The Supplier acknowledges that Venduro may rely upon such information when offering and selling Products to Buyers. Venduro’s publication or onward communication of Supplier-provided Product information does not constitute an independent representation or warranty by Venduro as to its accuracy, and Venduro’s liability to a Buyer arising from inaccurate Supplier-provided information is, in all cases, subject to the limitations in clause 51.</p>
                <h3 class="text-xl font-semibold text-gray-900">10. Exact Products</h3>
                <p>10.1. The Supplier must supply the exact GTIN ordered.</p>
                <p>10.2. The Supplier may not substitute:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>another GTIN;</li>
                    <li>another size;</li>
                    <li>another package quantity;</li>
                    <li>another formula;</li>
                    <li>another regional version;</li>
                    <li>another Product variant; or</li>
                    <li>another Product</li>
                </ul>
                <p>without Venduro’s prior written approval.</p>
                <p>10.3. A commercially similar, equivalent or replacement Product is not considered the same Product.</p>
                <h3 class="text-xl font-semibold text-gray-900">11. Supplier Product Warranties</h3>
                <p>For every Product supplied, the Supplier represents and warrants that:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>the Product is genuine and authentic;</li>
                    <li>the Product is new and unused unless expressly accepted otherwise;</li>
                    <li>the Product is suitable for lawful retail sale in the territory represented by the Supplier;</li>
                    <li>the Product is not counterfeit, replica or imitation;</li>
                    <li>the Product is not a tester unless expressly disclosed and accepted;</li>
                    <li>the Product has not been unlawfully decoded;</li>
                    <li>batch codes, serial numbers, GTINs, QR codes, traceability information or other identifiers have not been unlawfully removed, altered, concealed or tampered with;</li>
                    <li>the Supplier has full legal title and authority to sell the Product;</li>
                    <li>the Product is free from undisclosed liens, retention-of-title rights, security interests and third-party ownership claims;</li>
                    <li>the Product was lawfully acquired;</li>
                    <li>the Product is not stolen;</li>
                    <li>the Product has been stored, transported and handled appropriately;</li>
                    <li>the Product is not expired;</li>
                    <li>all material shelf-life limitations have been disclosed before PO acceptance;</li>
                    <li>the Product is not subject to an undisclosed recall, withdrawal, safety notice, sales prohibition or regulatory restriction;</li>
                    <li>all known geographical, channel or distribution restrictions have been disclosed to Venduro; and</li>
                    <li>all information supplied concerning the Product is materially accurate.</li>
                </ul>
                <h3 class="text-xl font-semibold text-gray-900">12. Shelf Life and Product Condition</h3>
                <p>12.1. Products must not be expired.</p>
                <p>12.2. Products must comply with any Product Quality or Shelf-Life Requirements communicated by Venduro and applicable when the relevant PO is accepted.</p>
                <p>12.3. Products normally sold:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>sealed;</li>
                    <li>cellophane wrapped;</li>
                    <li>tamper-evident; or</li>
                    <li>otherwise factory packaged</li>
                </ul>
                <p>must be supplied in the corresponding normal retail condition unless Venduro expressly accepts otherwise.</p>
                <p>12.4. Short-dated, clearance, damaged-packaging or otherwise non-standard Products may only be supplied where their relevant condition has been clearly disclosed and accepted before PO acceptance.</p>
                <p>12.5. Undisclosed short shelf life, damaged retail packaging or material deviation from normal retail condition constitutes Product non-conformity.</p>
                <h3 class="text-xl font-semibold text-gray-900">13. Regulatory Compliance</h3>
                <p>13.1. The Supplier represents and warrants that each Product complies with all mandatory laws and regulatory requirements applicable to the territory for which the Supplier has represented the Product as suitable.</p>
                <p>13.2. This includes, where applicable:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>product safety;</li>
                    <li>labelling;</li>
                    <li>traceability;</li>
                    <li>manufacturer information;</li>
                    <li>importer information;</li>
                    <li>Responsible Person information;</li>
                    <li>ingredients;</li>
                    <li>mandatory warnings;</li>
                    <li>language requirements;</li>
                    <li>registrations or notifications;</li>
                    <li>environmental requirements;</li>
                    <li>transport requirements;</li>
                    <li>dangerous-goods requirements; and</li>
                    <li>other Product-specific regulations.</li>
                </ul>
                <p>13.3. Unless expressly approved by Venduro in writing, the Supplier must not supply Products where doing so would require Venduro to become:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>the original EEA importer;</li>
                    <li>a cosmetics Responsible Person;</li>
                    <li>manufacturer;</li>
                    <li>authorised representative; or</li>
                    <li>another economic operator assuming materially greater regulatory responsibility than an ordinary distributor.</li>
                </ul>
                <p>13.4. If Products originate outside the EEA, the Supplier must disclose this before the relevant PO and provide information reasonably requested by Venduro concerning lawful importation and EEA market placement.</p>
                <p>13.5. The Supplier must immediately notify Venduro if it becomes aware of:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Product non-compliance;</li>
                    <li>regulatory investigation;</li>
                    <li>authority notice;</li>
                    <li>safety concern;</li>
                    <li>recall;</li>
                    <li>withdrawal;</li>
                    <li>counterfeit allegation;</li>
                    <li>IP complaint; or</li>
                    <li>other material issue</li>
                </ul>
                <p>affecting Products supplied or offered to Venduro.</p>
                <p>13.6. Nothing in these Terms excludes, transfers or limits any mandatory statutory duty imposed directly on Venduro.</p>
                <p>13.7. Where Venduro incurs liability, costs or losses because the Supplier breached its representations, warranties or obligations, Venduro retains its contractual and legal rights of recovery against the Supplier, including full legal and investigation costs reasonably incurred.</p>
                <h3 class="text-xl font-semibold text-gray-900">14. Cosmetics and Other Regulated Products</h3>
                <p>14.1. Where applicable, the Supplier must ensure that all legally required information and documentation is available, including:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>EU Responsible Person details;</li>
                    <li>manufacturer or importer details;</li>
                    <li>batch or lot information;</li>
                    <li>ingredient information;</li>
                    <li>mandatory warnings;</li>
                    <li>minimum durability or PAO information;</li>
                    <li>required local-language information;</li>
                    <li>required notifications or registrations; and</li>
                    <li>other Product-specific regulatory information.</li>
                </ul>
                <p>14.2. Venduro may request such information or evidence before or after purchasing Products.</p>
                <p>14.3. Venduro may reject, suspend, quarantine, return, stop selling or refuse further purchases of Products where Venduro reasonably identifies a compliance, safety, authenticity or regulatory concern.</p>
                <h3 class="text-xl font-semibold text-gray-900">15. EEA Sourcing and Intellectual Property Rights</h3>
                <p>15.1. The Supplier represents and warrants that:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Products are genuine;</li>
                    <li>Products were lawfully acquired;</li>
                    <li>the Supplier has lawful authority to sell them;</li>
                    <li>supply and resale do not infringe third-party intellectual property rights; and</li>
                    <li>the Supplier can reasonably evidence lawful sourcing.</li>
                </ul>
                <p>15.2. Unless Venduro expressly accepts otherwise, branded Products intended for EEA resale must have been lawfully placed on the EEA market by the relevant rights holder or with its consent.</p>
                <p>15.3. There must be no undisclosed legitimate basis known to the Supplier upon which the rights holder may lawfully oppose further commercialisation, including material alteration or impairment of Product condition.</p>
                <p>15.4. The Supplier must retain sufficient documentation to reasonably evidence lawful sourcing of Products supplied to Venduro.</p>
                <h3 class="text-xl font-semibold text-gray-900">16. Supply-Chain and Authenticity Documentation</h3>
                <p>16.1. Venduro may request evidence concerning:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>authenticity;</li>
                    <li>lawful sourcing;</li>
                    <li>intellectual property rights;</li>
                    <li>regulatory compliance;</li>
                    <li>Product safety;</li>
                    <li>chain of title; or</li>
                    <li>another legitimate compliance concern.</li>
                </ul>
                <p>16.2. Evidence may include:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Supplier purchase invoices;</li>
                    <li>upstream distributor invoices;</li>
                    <li>invoice chains;</li>
                    <li>delivery documents;</li>
                    <li>customs documentation;</li>
                    <li>import records;</li>
                    <li>certificates;</li>
                    <li>letters of authorisation;</li>
                    <li>EEA sourcing confirmations;</li>
                    <li>photographs;</li>
                    <li>serial information;</li>
                    <li>batch information; and</li>
                    <li>other relevant records.</li>
                </ul>
                <p>16.3. Following an authenticity, intellectual property, safety or regulatory claim, the Supplier must provide all available supporting documentation within 48 hours after Venduro’s request.</p>
                <p>16.4. Where all requested documentation cannot reasonably be obtained within 48 hours, the Supplier must:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>provide all available documentation within that period;</li>
                    <li>identify what remains outstanding;</li>
                    <li>explain the reason for the delay; and</li>
                    <li>provide the remaining documentation without undue delay.</li>
                </ul>
                <p>16.5. Documentation must be sufficiently legible to identify the relevant:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Products;</li>
                    <li>quantities;</li>
                    <li>parties;</li>
                    <li>transactions; and</li>
                    <li>chain of supply.</li>
                </ul>
                <p>16.6. Commercial prices may be redacted unless those prices are reasonably necessary to investigate the relevant issue.</p>
                <p>16.7. Party identities and transaction details necessary to establish authenticity or lawful sourcing may not be redacted.</p>
                <p>16.8. Venduro may use or disclose relevant documentation where reasonably necessary to:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>defend itself;</li>
                    <li>respond to Buyers;</li>
                    <li>respond to marketplaces;</li>
                    <li>respond to rights holders;</li>
                    <li>respond to insurers;</li>
                    <li>obtain legal advice;</li>
                    <li>investigate fraud;</li>
                    <li>enforce these Terms;</li>
                    <li>manage a recall; or</li>
                    <li>comply with legal or regulatory requirements.</li>
                </ul>
                <p>16.9. Failure to provide sufficient evidence may result in suspension of Products, Orders or related payments, and shall be treated as a material risk factor for the purposes of clause 24 (Payment Hold and Reserve).</p>
                <p>16.10. Failure to provide documentation does not by itself constitute a final determination that Products are counterfeit, but Venduro may treat the absence of evidence as a material risk factor.</p>
                <h3 class="text-xl font-semibold text-gray-900">17. Intellectual Property and Authenticity Claims</h3>
                <p>17.1. If Venduro receives a credible authenticity or intellectual property claim, Venduro may without prior notice:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>suspend affected Products;</li>
                    <li>suspend performance of affected transactions;</li>
                    <li>quarantine Products;</li>
                    <li>request additional documentation;</li>
                    <li>temporarily withhold related Supplier payments;</li>
                    <li>restrict the Supplier account; and</li>
                    <li>take other reasonable steps to prevent further exposure.</li>
                </ul>
                <p>17.2. Where Products are reasonably established to be counterfeit, infringing, unlawfully sourced, materially altered or otherwise materially non-compliant, Venduro may:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>cancel affected Orders;</li>
                    <li>refund or compensate Buyers;</li>
                    <li>return Products;</li>
                    <li>arrange destruction where reasonably required;</li>
                    <li>perform or participate in recalls;</li>
                    <li>suspend or terminate the Supplier;</li>
                    <li>recover losses from the Supplier;</li>
                    <li>disclose relevant information where legally justified; and</li>
                    <li>cooperate with authorities, marketplaces or rights holders.</li>
                </ul>
                <h3 class="text-xl font-semibold text-gray-900">18. Supplier Notification Duties</h3>
                <p>The Supplier must notify Venduro immediately and, in any event, no later than 24 hours after becoming aware of any material:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>recall;</li>
                    <li>authority investigation;</li>
                    <li>Product safety concern;</li>
                    <li>intellectual property claim;</li>
                    <li>authenticity claim;</li>
                    <li>suspected counterfeit issue;</li>
                    <li>unlawful sourcing issue;</li>
                    <li>regulatory prohibition; or</li>
                    <li>similar event</li>
                </ul>
                <p>affecting Products supplied or offered to Venduro.</p>
                <h3 class="text-xl font-semibold text-gray-900">19. Fulfilment</h3>
                <p>19.1. Accepted POs must be fulfilled accurately and in full unless Venduro agrees otherwise.</p>
                <p>19.2. The Supplier must comply with the agreed lead time.</p>
                <p>19.3. The Supplier must notify Venduro immediately if fulfilment may be delayed, incomplete or otherwise deviate from the accepted PO.</p>
                <p>19.4. Partial shipment is permitted only with Venduro’s approval.</p>
                <p>19.5. Venduro may require specific packaging, labelling, palletisation, documentation or shipment-preparation requirements.</p>
                <h3 class="text-xl font-semibold text-gray-900">20. Packaging</h3>
                <p>20.1. Products must be packed appropriately for wholesale transportation.</p>
                <p>20.2. The Supplier is responsible for losses reasonably resulting from inadequate packaging, packing, palletisation or labelling attributable to the Supplier.</p>
                <p>20.3. Unless instructed otherwise, direct shipments to Buyers must not contain:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Supplier invoices;</li>
                    <li>Supplier price lists;</li>
                    <li>Supplier marketing materials;</li>
                    <li>Supplier sales solicitations; or</li>
                    <li>documentation revealing confidential Supplier commercial terms.</li>
                </ul>
                <p>20.4. Packaging must be neutral where reasonably requested by Venduro.</p>
                <p>20.5. Separate POs must not be combined without Venduro’s permission.</p>
                <h3 class="text-xl font-semibold text-gray-900">21. Shipping and Customs Documentation</h3>
                <p>21.1. The Supplier must provide accurate information and documentation reasonably required for transport or customs purposes, including where applicable:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>customs codes;</li>
                    <li>HS codes;</li>
                    <li>country of origin;</li>
                    <li>export documentation;</li>
                    <li>commercial invoices;</li>
                    <li>certificates of origin;</li>
                    <li>SDS/MSDS information;</li>
                    <li>dangerous-goods declarations; and</li>
                    <li>other customs or carrier documentation.</li>
                </ul>
                <p>21.2. Urgent carrier or customs requests must be responded to as soon as reasonably possible and normally within 24 hours.</p>
                <h3 class="text-xl font-semibold text-gray-900">22. Supplier-to-Venduro Risk and Title</h3>
                <p>22.1. Risk passes according to the Incoterm or delivery condition stated in the applicable PO.</p>
                <p>22.2. If no specific delivery condition is stated, risk passes from the Supplier to Venduro when the Products are:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>delivered to the location nominated by Venduro; or</li>
                    <li>where collection is arranged by Venduro, handed over to Venduro’s nominated carrier.</li>
                </ul>
                <p>22.3. Unless expressly agreed otherwise, title to Products passes to Venduro when risk passes.</p>
                <p>22.4. Products must pass to Venduro free from:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>retention of title;</li>
                    <li>lien;</li>
                    <li>pledge;</li>
                    <li>security interest;</li>
                    <li>encumbrance; and</li>
                    <li>third-party ownership claims.</li>
                </ul>
                <p>22.5. Any Supplier retention-of-title provision is excluded unless expressly accepted by Venduro in writing.</p>
                <p>22.6. For direct Supplier-to-Buyer shipments, title must pass from the Supplier to Venduro no later than immediately before title passes from Venduro to the Buyer.</p>
                <h3 class="text-xl font-semibold text-gray-900">23. Supplier Invoices and Payments</h3>
                <p>23.1. Supplier payment terms are those stated in:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>the applicable PO;</li>
                    <li>a separate written Supplier agreement; or</li>
                    <li>if neither applies, the payment conditions expressly confirmed by Venduro.</li>
                </ul>
                <p>23.2. Except where the applicable PO requires prepayment or otherwise expressly provides for payment before Delivery, and subject to mandatory applicable payment rules, any payment period calculated after Delivery starts only after Venduro has received:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>complete delivery of the relevant Products;</li>
                    <li>a legally compliant invoice; and</li>
                    <li>documentation expressly required under the applicable PO or reasonably necessary to verify conformity of the delivery.</li>
                </ul>
                <p>23.3. If an invoice is incorrect, incomplete or materially inconsistent with the applicable PO, the relevant payment period starts when Venduro receives a corrected legally compliant invoice, subject to mandatory law.</p>
                <p>23.4. Venduro is not obliged to pay amounts genuinely disputed in good faith while the relevant dispute is being reasonably investigated.</p>
                <p>23.5. Payment by Venduro does not constitute:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>acceptance of Products;</li>
                    <li>confirmation of conformity;</li>
                    <li>waiver of defects;</li>
                    <li>waiver of claims;</li>
                    <li>confirmation of authenticity; or</li>
                    <li>acknowledgement that the Supplier has fully complied with these Terms.</li>
                </ul>
                <h3 class="text-xl font-semibold text-gray-900">24. Payment Hold and Reserve</h3>
                <p>24.1. Venduro may temporarily withhold or reserve an amount reasonably corresponding to its potential exposure where there is a credible or reasonably suspected claim of any of the following types:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Supplier-at-Fault Claim;</li>
                    <li>authenticity claim;</li>
                    <li>intellectual property claim;</li>
                    <li>regulatory claim;</li>
                    <li>Buyer claim;</li>
                    <li>chargeback;</li>
                    <li>recall;</li>
                    <li>tax issue; or</li>
                    <li>other claim attributable or potentially attributable to the Supplier.</li>
                </ul>
                <p>24.2. Venduro may apply a reserve against amounts otherwise payable to the same Supplier even where those amounts relate to different POs, and may aggregate reserves across multiple concurrent or related claims.</p>
                <p>24.3. A payment hold or reserve does not constitute a final determination of Supplier liability.</p>
                <p>24.4. The amount withheld must be reasonably related to Venduro’s anticipated exposure, including reasonably estimated investigation, replacement, recall and legal costs, as assessed by Venduro in good faith.</p>
                <h3 class="text-xl font-semibold text-gray-900">25. Set-Off</h3>
                <p>25.1. To the fullest extent permitted by applicable law, Venduro may set off any amount owed by the Supplier against any amount payable by Venduro to that Supplier.</p>
                <p>25.2. This includes amounts:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>admitted by the Supplier;</li>
                    <li>confirmed by agreement;</li>
                    <li>reasonably and in good faith determined by Venduro to be due, including under the procedure in clause 26.6–26.7;</li>
                    <li>finally determined under an applicable agreed claims process;</li>
                    <li>resulting from a final court or authority decision; or</li>
                    <li>otherwise legally due and enforceable.</li>
                </ul>
                <p>25.3. Rights under this clause are without prejudice to any other legal or contractual remedy available to Venduro.</p>
                <h3 class="text-xl font-semibold text-gray-900">26. Supplier-at-Fault Claims</h3>
                <p>26.1. The Supplier is responsible where a claim, loss or non-conformity arises from circumstances attributable to the Supplier, including:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>shortages;</li>
                    <li>overstated stock;</li>
                    <li>incorrect Products;</li>
                    <li>incorrect GTINs;</li>
                    <li>incorrect quantities;</li>
                    <li>inadequate packaging;</li>
                    <li>transport damage caused by Supplier packaging;</li>
                    <li>counterfeit Products;</li>
                    <li>decoded Products;</li>
                    <li>tampered Products;</li>
                    <li>expired Products;</li>
                    <li>undisclosed short shelf life;</li>
                    <li>regulatory non-compliance;</li>
                    <li>intellectual property infringement;</li>
                    <li>unlawful sourcing;</li>
                    <li>inaccurate customs documentation;</li>
                    <li>false or misleading documentation;</li>
                    <li>late fulfilment;</li>
                    <li>cancellation of an accepted PO; or</li>
                    <li>material breach of these Terms.</li>
                </ul>
                <p>26.2. Venduro may recover reasonable losses arising from a Supplier-at-Fault Claim, including where applicable:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>purchase price;</li>
                    <li>Buyer refunds;</li>
                    <li>Buyer credits;</li>
                    <li>replacement costs;</li>
                    <li>outbound transportation;</li>
                    <li>return transportation;</li>
                    <li>re-shipping costs;</li>
                    <li>storage;</li>
                    <li>customs costs;</li>
                    <li>taxes directly resulting from Supplier error;</li>
                    <li>destruction costs;</li>
                    <li>recall costs;</li>
                    <li>reasonable third-party charges;</li>
                    <li>marketplace penalties;</li>
                    <li>reasonable external legal costs;</li>
                    <li>reasonable investigation costs; and</li>
                    <li>Venduro’s lost gross margin on the affected transaction.</li>
                </ul>
                <p>26.3. Recoverable losses must be reasonably attributable to the Supplier’s breach and reasonably documented.</p>
                <p>26.4. Venduro shall take reasonable steps to mitigate recoverable losses.</p>
                <p>26.5. Venduro shall not recover the same loss more than once.</p>
                <p>26.6. Unless urgent action is required, the Supplier will normally have 5 Business Days after notification to provide a reasoned dispute and supporting evidence.</p>
                <p>26.7. If the Supplier fails to provide a reasoned dispute within that period, the claim is treated as undisputed and due for all purposes under these Terms, including set-off under clause 25 and reserve under clause 24.</p>
                <p>26.8. Venduro’s right to bring a Supplier-at-Fault Claim under this clause in respect of Products the subject of a Buyer claim under clause 40 or 41 arises immediately upon Venduro’s acceptance, or reasonable determination, of the underlying Buyer claim, and is not limited or delayed by the Buyer claim windows in clauses 40.2 or 41.2.</p>
                <h3 class="text-xl font-semibold text-gray-900">27. Recalls</h3>
                <p>27.1. The Supplier must immediately cooperate with any recall, withdrawal or safety action concerning Products supplied by it.</p>
                <p>27.2. Where such action results from circumstances attributable to the Supplier, the Supplier is responsible for reasonable:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Product costs;</li>
                    <li>notification costs;</li>
                    <li>transportation costs;</li>
                    <li>storage costs;</li>
                    <li>retrieval costs;</li>
                    <li>replacement costs;</li>
                    <li>disposal costs;</li>
                    <li>destruction costs; and</li>
                    <li>third-party costs.</li>
                </ul>
                <p>27.3. Venduro may conduct, coordinate or participate in a recall itself where reasonably necessary to protect Buyers, end customers, the public, Venduro or legal compliance.</p>
                <h3 class="text-xl font-semibold text-gray-900">28. Supplier Indemnity</h3>
                <p>28.1. The Supplier shall indemnify and hold Venduro harmless against any and all third-party claims, liabilities, losses, penalties and costs (including full legal costs on an indemnity basis and investigation costs) to the extent arising from:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>counterfeit Products;</li>
                    <li>absence of lawful title;</li>
                    <li>intellectual property infringement;</li>
                    <li>unlawful sourcing;</li>
                    <li>Product non-compliance;</li>
                    <li>product-safety breach;</li>
                    <li>materially inaccurate regulatory information;</li>
                    <li>false sourcing documentation;</li>
                    <li>tax or customs documentation attributable to the Supplier;</li>
                    <li>fraud;</li>
                    <li>wilful misconduct; or</li>
                    <li>breach of Supplier warranties contained in these Terms.</li>
                </ul>
                <p>28.2. Venduro shall take reasonable steps to mitigate recoverable losses.</p>
                <p>28.3. This indemnity is in addition to other rights available to Venduro but does not permit double recovery of the same loss.</p>
                <p>28.4. Supplier warranties and obligations relating to:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Product conformity;</li>
                    <li>authenticity;</li>
                    <li>lawful sourcing;</li>
                    <li>intellectual property;</li>
                    <li>regulatory compliance;</li>
                    <li>claims;</li>
                    <li>indemnification; and</li>
                    <li>payment</li>
                </ul>
                <p>survive delivery, completion, payment, termination and closure of the Supplier account for the applicable statutory limitation period or such longer period as expressly agreed.</p>
                <h3 class="text-xl font-semibold text-gray-900">29. Supplier Insurance</h3>
                <p>29.1. Suppliers must maintain insurance reasonably appropriate to the nature and scale of their business and Products.</p>
                <p>29.2. Venduro may request evidence of appropriate:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>product liability insurance;</li>
                    <li>commercial liability insurance;</li>
                    <li>recall insurance; or</li>
                    <li>other relevant insurance.</li>
                </ul>
                <p>29.3. Failure to provide requested evidence of insurance within a reasonable period set by Venduro, or failure to maintain insurance reasonably requested by Venduro, entitles Venduro to immediately restrict, suspend or terminate the Supplier’s access to the Platform in accordance with clause 50, without a prior cure period.</p>
                <h2 class="text-2xl font-semibold text-gray-900 pt-4">PART III – BUYER TERMS</h2>
                <h3 class="text-xl font-semibold text-gray-900">30. Buyer Orders and Acceptance</h3>
                <p>30.1. Products displayed on the Platform are invitations to submit an Order and do not constitute binding offers by Venduro.</p>
                <p>30.2. A Buyer Order constitutes an offer by the Buyer to purchase Products from Venduro.</p>
                <p>30.3. An automated acknowledgement, Order Received status, payment authorisation or receipt of funds does not by itself constitute acceptance by Venduro.</p>
                <p>30.4. A Buyer Order becomes binding on Venduro only when:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Venduro changes the relevant status to Order Confirmed; or</li>
                    <li>Venduro otherwise expressly confirms acceptance in writing.</li>
                </ul>
                <p>30.5. Venduro may accept:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>the whole Buyer Order;</li>
                    <li>individual Order lines;</li>
                    <li>reduced quantities; or</li>
                    <li>only part of the Buyer Order.</li>
                </ul>
                <p>30.6. Products or quantities not expressly confirmed are not binding on Venduro.</p>
                <p>30.7. Venduro may reject any Buyer Order before acceptance, including for stock availability, compliance, fraud, sanctions, credit-risk, commercial-risk or other legitimate business reasons.</p>
                <h3 class="text-xl font-semibold text-gray-900">31. Cancellation or Adjustment After Confirmation</h3>
                <p>31.1. After an Order has been confirmed, the Buyer may not cancel or amend it without Venduro’s prior written approval.</p>
                <p>31.2. Venduro may cancel or reduce affected Order lines after confirmation where reasonably necessary because of:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>a material authenticity concern;</li>
                    <li>Product safety concern;</li>
                    <li>regulatory restriction;</li>
                    <li>intellectual property claim;</li>
                    <li>government or authority action;</li>
                    <li>manifest pricing or data error;</li>
                    <li>Product recall;</li>
                    <li>Supplier insolvency;</li>
                    <li>exceptional Supplier failure;</li>
                    <li>destruction or loss of Products before risk has passed;</li>
                    <li>legal prohibition;</li>
                    <li>sanctions restrictions;</li>
                    <li>customs prohibition;</li>
                    <li>extraordinary transportation failure; or</li>
                    <li>another circumstance that makes fulfilment unlawful, impossible or prevented by circumstances beyond Venduro’s reasonable control.</li>
                </ul>
                <p>31.3. Where Venduro cancels under this clause, Venduro’s obligation concerning the cancelled Products shall, subject to mandatory law, normally be limited to:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>refunding amounts paid for the cancelled Products; or</li>
                    <li>issuing an agreed credit.</li>
                </ul>
                <p>31.4. Cancellation of one Product or Order line does not entitle the Buyer to cancel other confirmed Order lines unless Venduro agrees otherwise.</p>
                <p>31.5. Nothing in this clause excludes liability that cannot lawfully be limited.</p>
                <h3 class="text-xl font-semibold text-gray-900">32. Consolidated and Multi-Supplier Orders</h3>
                <p>32.1. Venduro may source a Buyer Order from multiple Suppliers.</p>
                <p>32.2. Products may be:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>purchased under separate Supplier POs;</li>
                    <li>delivered at different times;</li>
                    <li>delivered to different logistics facilities;</li>
                    <li>checked, processed or repacked by Venduro or its logistics providers; and</li>
                    <li>consolidated into one outbound shipment.</li>
                </ul>
                <p>32.3. The Buyer acknowledges that consolidated Orders may require longer lead times.</p>
                <p>32.4. Delay affecting one component of a consolidated Order may delay dispatch of the consolidated shipment.</p>
                <p>32.5. Unless expressly agreed otherwise or disclosure is reasonably required for fulfilment, authenticity verification, legal, customs, regulatory or compliance purposes, Venduro is not required to disclose:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Supplier identity;</li>
                    <li>Supplier contact information;</li>
                    <li>Supplier purchase price;</li>
                    <li>Supplier stock source;</li>
                    <li>upstream sourcing identity; or</li>
                    <li>Venduro’s margin.</li>
                </ul>
                <p>32.6. Where reasonably necessary to demonstrate authenticity or lawful sourcing, Venduro may provide redacted sourcing documentation while protecting confidential Supplier information.</p>
                <h3 class="text-xl font-semibold text-gray-900">33. Prices</h3>
                <p>33.1. The applicable price is the price stated in the Order Confirmation.</p>
                <p>33.2. Prices exclude VAT unless expressly stated otherwise.</p>
                <p>33.3. Prices displayed before Order confirmation may change.</p>
                <p>33.4. Venduro is not bound by manifest:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>typographical errors;</li>
                    <li>decimal errors;</li>
                    <li>currency errors;</li>
                    <li>API errors;</li>
                    <li>feed errors;</li>
                    <li>software errors; or</li>
                    <li>obvious pricing mistakes.</li>
                </ul>
                <p>33.5. Where Venduro identifies a manifest pricing or data error before delivery, Venduro may cancel the affected Order line and refund any related payment.</p>
                <h3 class="text-xl font-semibold text-gray-900">34. VAT and Tax Information</h3>
                <p>34.1. The Buyer is responsible for supplying complete and accurate:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>legal name;</li>
                    <li>company details;</li>
                    <li>VAT number;</li>
                    <li>billing address;</li>
                    <li>delivery address; and</li>
                    <li>other tax information requested by Venduro.</li>
                </ul>
                <p>34.2. Zero-rated or reverse-charge VAT treatment applies only where all applicable legal requirements are satisfied.</p>
                <p>34.3. Where VAT treatment depends on evidence or declarations from the Buyer, including transport evidence, the Buyer must provide such evidence promptly upon request.</p>
                <p>34.4. Where the Buyer or its carrier collects Products, the Buyer must provide reasonable evidence of intra-EU transportation or export where required.</p>
                <p>34.5. If VAT, duties, interest, penalties or related charges are assessed against Venduro as a direct or indirect result of:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>materially incorrect Buyer information;</li>
                    <li>false declarations;</li>
                    <li>misuse of a VAT number; or</li>
                    <li>failure to provide legally required transport or tax evidence,</li>
                </ul>
                <p>the Buyer shall reimburse Venduro in full for the amounts attributable to that failure, including any penalties and interest assessed against Venduro.</p>
                <h3 class="text-xl font-semibold text-gray-900">35. Buyer Payment</h3>
                <p>35.1. Unless expressly agreed otherwise, Venduro may require:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>full prepayment;</li>
                    <li>deposit;</li>
                    <li>payment before dispatch;</li>
                    <li>approved deferred payment terms;</li>
                    <li>bank guarantee;</li>
                    <li>credit insurance support; or</li>
                    <li>another reasonable form of payment security.</li>
                </ul>
                <p>35.2. Venduro may determine and modify for future Orders, at any time and at its discretion:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>credit limits;</li>
                    <li>deferred-payment availability;</li>
                    <li>required deposits;</li>
                    <li>payment methods; and</li>
                    <li>other credit conditions.</li>
                </ul>
                <p>35.3. Deferred payment is a discretionary credit facility and may be withdrawn or reduced for future Orders based on payment history, credit risk or other reasonable commercial factors, without prior notice.</p>
                <p>35.4. The Buyer must pay all undisputed amounts on or before the applicable due date.</p>
                <p>35.5. Payment obligations are not conditional upon the Buyer successfully reselling the Products or receiving payment from its own customers.</p>
                <p>35.6. The Buyer may not withhold payment or set off an unrelated claim against amounts owed to Venduro unless Venduro agrees in writing or such right cannot lawfully be excluded.</p>
                <p>35.7. Overdue amounts accrue statutory commercial late-payment interest.</p>
                <p>35.8. Venduro may additionally recover legally available debt-recovery compensation and reasonable recovery costs.</p>
                <p>35.9. Where an undisputed overdue amount exists, Venduro may, without prior notice:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>suspend new Orders;</li>
                    <li>suspend dispatch;</li>
                    <li>revoke credit terms;</li>
                    <li>reduce the Buyer’s credit limit;</li>
                    <li>require prepayment;</li>
                    <li>withhold future shipments; or</li>
                    <li>suspend the Buyer’s account.</li>
                </ul>
                <h3 class="text-xl font-semibold text-gray-900">36. Delivery</h3>
                <p>36.1. Applicable delivery conditions will be stated in the Order Confirmation, quotation, invoice or other written confirmation.</p>
                <p>36.2. References to Incoterms mean Incoterms® 2020 unless expressly stated otherwise.</p>
                <p>36.3. Delivery dates and lead times are estimates unless Venduro expressly confirms a guaranteed date in writing.</p>
                <p>36.4. The Buyer is responsible for:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>providing an accurate delivery address;</li>
                    <li>ensuring that delivery can legally and physically be completed;</li>
                    <li>providing customs or import information where required;</li>
                    <li>ensuring that an authorised person can receive the Products;</li>
                    <li>arranging unloading where reasonably required; and</li>
                    <li>paying additional costs resulting from circumstances attributable to the Buyer.</li>
                </ul>
                <p>36.5. Storage, redelivery, return-to-sender, waiting-time, customs, unsuccessful delivery or carrier charges arising from circumstances attributable to the Buyer may be charged to the Buyer.</p>
                <h3 class="text-xl font-semibold text-gray-900">37. Buyer Risk and Title</h3>
                <p>37.1. Risk passes according to the agreed Incoterm or delivery condition.</p>
                <p>37.2. If no specific delivery condition is stated, risk passes when Products are handed to the first carrier responsible for transportation to the Buyer.</p>
                <p>37.3. To the extent permitted and effective under applicable law, title remains with Venduro until Venduro receives full payment for the relevant Products.</p>
                <p>37.4. A Buyer granted deferred payment terms may resell Products in the ordinary course of its business but remains fully responsible for payment to Venduro.</p>
                <p>37.5. Retention of title is additional credit protection and does not limit any other right, claim or security available to Venduro.</p>
                <h3 class="text-xl font-semibold text-gray-900">38. Product Identification</h3>
                <p>38.1. GTIN is the primary Product identifier used by Venduro.</p>
                <p>38.2. Venduro will supply the GTIN confirmed in the Order unless the Buyer expressly accepts a substitution.</p>
                <p>38.3. Product images are illustrative.</p>
                <p>38.4. Packaging, design, artwork or other non-material presentation elements may vary between production batches without constituting non-conformity where:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>the ordered GTIN is correct; and</li>
                    <li>the Product is otherwise materially as described.</li>
                </ul>
                <h3 class="text-xl font-semibold text-gray-900">39. Buyer’s Resale Responsibility</h3>
                <p>39.1. The Buyer is responsible for legal and commercial obligations arising from its onward resale activities, including:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>its chosen resale country;</li>
                    <li>sales channel;</li>
                    <li>marketplace;</li>
                    <li>advertisements;</li>
                    <li>tax position;</li>
                    <li>import obligations applicable to the Buyer; and</li>
                    <li>obligations towards its own customers.</li>
                </ul>
                <p>39.2. The Buyer must comply with country, channel, brand or distribution restrictions expressly communicated by Venduro.</p>
                <p>39.3. The Buyer must not knowingly export or resell Products into a territory or channel expressly identified by Venduro as restricted.</p>
                <p>39.4. Venduro does not guarantee that Products will be accepted by:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Amazon;</li>
                    <li>eBay;</li>
                    <li>Allegro;</li>
                    <li>Kaufland;</li>
                    <li>another marketplace; or</li>
                    <li>any specific customer of the Buyer,</li>
                </ul>
                <p>unless Venduro expressly provides such guarantee in writing. This exclusion applies regardless of the underlying cause of non-acceptance, including where the cause relates to Supplier-provided information later found to be inaccurate, subject always to clause 51.</p>
                <p>39.5. Nothing in this clause excludes any mandatory statutory obligation imposed directly on Venduro.</p>
                <h3 class="text-xl font-semibold text-gray-900">40. Inspection and Shipment Claims</h3>
                <p>40.1. The Buyer must inspect the Products and shipment promptly upon Delivery.</p>
                <p>40.2. All claims relating to defects, damage or discrepancies that were, or reasonably should have been, identifiable upon ordinary inspection of the shipment must be submitted to Venduro within 5 Business Days after Delivery.</p>
                <p>40.3. The 5 Business Day claim period applies in particular to:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>damaged Products;</li>
                    <li>transport damage;</li>
                    <li>visibly defective Products;</li>
                    <li>damaged retail packaging;</li>
                    <li>damaged shipping cartons;</li>
                    <li>damaged pallets;</li>
                    <li>missing Products;</li>
                    <li>shortages;</li>
                    <li>incorrect quantities;</li>
                    <li>incorrect Products;</li>
                    <li>incorrect GTINs;</li>
                    <li>incorrect pack sizes;</li>
                    <li>visible leakage or breakage; and</li>
                    <li>other apparent shipment, quantity, packaging or fulfilment discrepancies.</li>
                </ul>
                <p>40.4. Claims concerning matters listed in clause 40.3 submitted after the expiry of the 5 Business Day period may be rejected by Venduro to the fullest extent permitted by applicable law.</p>
                <p>40.5. In determining a late claim, Venduro may take into account whether the delay has impaired or prevented Venduro’s ability to:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>verify the claimed discrepancy;</li>
                    <li>establish when or how damage occurred;</li>
                    <li>inspect the Products;</li>
                    <li>verify quantities;</li>
                    <li>make a claim against a Supplier;</li>
                    <li>make a claim against a carrier;</li>
                    <li>obtain evidence; or</li>
                    <li>otherwise investigate or remedy the claim.</li>
                </ul>
                <p>40.6. The Buyer must provide all evidence reasonably requested by Venduro, including where applicable:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>photographs of the unopened shipment;</li>
                    <li>outer packaging photographs;</li>
                    <li>pallet photographs;</li>
                    <li>shipping labels;</li>
                    <li>carrier labels;</li>
                    <li>Product photographs;</li>
                    <li>video evidence;</li>
                    <li>quantity counts;</li>
                    <li>GTINs;</li>
                    <li>batch or lot numbers;</li>
                    <li>expiry information;</li>
                    <li>packing documents;</li>
                    <li>proof of delivery; and</li>
                    <li>other reasonably relevant evidence.</li>
                </ul>
                <p>40.7. Where damage is apparent at the time of Delivery, the Buyer must, where reasonably possible:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>record the damage on the carrier’s delivery document or electronic proof of delivery;</li>
                    <li>notify the carrier;</li>
                    <li>retain the damaged packaging; and</li>
                    <li>obtain photographic evidence before unpacking or disposing of packaging.</li>
                </ul>
                <p>40.8. Failure to record obvious transport damage at Delivery may be taken into account when assessing the claim and may justify rejection of the claim where such failure materially prejudices Venduro’s or the carrier’s ability to establish responsibility.</p>
                <p>40.9. Products and relevant packaging relating to a claim must be preserved until Venduro confirms that they may be disposed of.</p>
                <p>40.10. The 5 Business Day period under this clause does not apply to a latent defect that could not reasonably have been identified during ordinary inspection following Delivery. Such defects are governed by clause 41.</p>
                <h3 class="text-xl font-semibold text-gray-900">41. Latent Defects and Other Non-Apparent Claims</h3>
                <p>41.1. A defect is considered latent for the purposes of these Terms only where it could not reasonably have been identified during ordinary inspection of the Products following Delivery.</p>
                <p>41.2. The Buyer must notify Venduro of any alleged latent defect within 5 Business Days after the defect was discovered or reasonably should have been discovered.</p>
                <p>41.3. Failure to provide timely notification may result in rejection or limitation of the claim to the extent that the delay materially prejudices Venduro’s ability to:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>investigate the claim;</li>
                    <li>establish its cause;</li>
                    <li>inspect the Products;</li>
                    <li>obtain evidence;</li>
                    <li>seek recourse against the Supplier or another responsible party; or</li>
                    <li>provide an appropriate remedy.</li>
                </ul>
                <p>41.4. Nothing in this clause extends any statutory, contractual or limitation period otherwise applicable to a claim.</p>
                <p>41.5. Authenticity, Product safety, regulatory or intellectual property concerns must be reported immediately after discovery.</p>
                <p>41.6. The Buyer must preserve affected Products, packaging, batch information and other relevant evidence while the claim is being investigated.</p>
                <p>41.7. Products subject to an authenticity, safety or regulatory investigation must not knowingly be:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>resold;</li>
                    <li>altered;</li>
                    <li>destroyed; or</li>
                    <li>disposed of</li>
                </ul>
                <p>without Venduro’s approval, except where immediate action is required by law or a competent authority.</p>
                <h3 class="text-xl font-semibold text-gray-900">42. Buyer Claim Remedies</h3>
                <p>42.1. Venduro may investigate any claim and request additional evidence before determining whether the claim is accepted.</p>
                <p>42.2. Where a claim is accepted, Venduro may, at its reasonable discretion and subject to applicable mandatory law, provide an appropriate remedy including:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>replacement;</li>
                    <li>repair where appropriate;</li>
                    <li>partial refund;</li>
                    <li>full refund;</li>
                    <li>credit note;</li>
                    <li>price reduction; or</li>
                    <li>another commercially reasonable remedy.</li>
                </ul>
                <p>42.3. Venduro may require return of the affected Products.</p>
                <p>42.4. Unless otherwise agreed or required by mandatory law, Venduro is not required to reimburse return, disposal, destruction, storage or other costs incurred by the Buyer without Venduro’s prior approval.</p>
                <p>42.5. There is no general right to return Products because:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>the Buyer changed its mind;</li>
                    <li>the Buyer no longer needs the Products;</li>
                    <li>market prices decreased;</li>
                    <li>resale was slower than expected;</li>
                    <li>the Buyer’s customer cancelled;</li>
                    <li>the Buyer purchased excessive quantities;</li>
                    <li>the Buyer’s commercial strategy changed; or</li>
                    <li>a third-party marketplace refused the Products for reasons not attributable to Venduro.</li>
                </ul>
                <h2 class="text-2xl font-semibold text-gray-900 pt-4">PART IV – CONFIDENTIALITY, DATA AND PLATFORM USE</h2>
                <h3 class="text-xl font-semibold text-gray-900">43. Confidential Information</h3>
                <p>43.1. Non-public commercial information obtained through Venduro is confidential.</p>
                <p>43.2. Confidential Information includes, without limitation:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Supplier identity where not publicly disclosed;</li>
                    <li>Buyer identity where not publicly disclosed;</li>
                    <li>sourcing information;</li>
                    <li>prices;</li>
                    <li>purchase prices;</li>
                    <li>margins;</li>
                    <li>stock information;</li>
                    <li>feed information;</li>
                    <li>customer information;</li>
                    <li>supply-chain documentation;</li>
                    <li>Order history;</li>
                    <li>API information;</li>
                    <li>sales information; and</li>
                    <li>other commercially sensitive information.</li>
                </ul>
                <p>43.3. Confidential Information may only be used or disclosed for legitimate purposes connected with the relevant business relationship with Venduro or as otherwise expressly permitted under these Terms.</p>
                <p>43.4. Confidentiality obligations do not apply to information that:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>is lawfully public;</li>
                    <li>was already lawfully known without confidentiality restrictions;</li>
                    <li>was independently developed without use of Confidential Information; or</li>
                    <li>must be disclosed by applicable law or competent authority.</li>
                </ul>
                <h3 class="text-xl font-semibold text-gray-900">44. Data Protection</h3>
                <p>44.1. Where a Supplier or Buyer receives personal data of the other party’s personnel, or of the other party’s end customers, for fulfilment purposes, that party may use it only for:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>delivery;</li>
                    <li>transportation;</li>
                    <li>customs;</li>
                    <li>legally required compliance;</li>
                    <li>legitimate Order-related communication; and</li>
                    <li>claim handling.</li>
                </ul>
                <p>44.2. The Supplier and Buyer must not use personal data obtained through Venduro for:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>marketing;</li>
                    <li>solicitation;</li>
                    <li>prospecting;</li>
                    <li>unrelated sales;</li>
                    <li>database enrichment;</li>
                    <li>resale of contact data; or</li>
                    <li>circumvention of Venduro.</li>
                </ul>
                <p>44.3. The Supplier and the Buyer shall each be an independent data controller in respect of personal data of their own personnel, agents and end customers, and shall each be solely responsible, as between the parties, for compliance with applicable data protection law in respect of the personal data they process in connection with the Platform, including personal data received from or via Venduro for fulfilment purposes. Each party shall indemnify Venduro against losses arising from that party’s breach of applicable data protection law.</p>
                <p>44.4. Each party must comply with applicable data-protection law.</p>
                <p>44.5. Personal data processed directly by Venduro in its own capacity as controller (for example, Supplier and Buyer account and contact data) is additionally governed by Venduro’s applicable Privacy Policy.</p>
                <p>44.6. Nothing in this clause overrides a mandatory statutory allocation of controller or processor status under applicable data protection law.</p>
                <h3 class="text-xl font-semibold text-gray-900">45. Non-Circumvention</h3>
                <p>45.1. Users must not use information, introductions or relationships obtained through Venduro primarily to bypass Venduro.</p>
                <p>45.2. Suppliers must not directly solicit or transact outside Venduro with a Buyer first introduced to them through Venduro where the purpose or effect is to avoid Venduro.</p>
                <p>45.3. Buyers must not use confidential Supplier information received through Venduro to bypass Venduro.</p>
                <p>45.4. These restrictions apply:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>during the relevant business relationship; and</li>
                    <li>for 24 months following the last relevant Order or introduction.</li>
                </ul>
                <p>45.5. The restriction does not apply where a party can demonstrate, by way of documentary evidence dated prior to the relevant introduction and provided to Venduro promptly upon request, a genuine pre-existing commercial relationship with the relevant counterparty that existed independently of Venduro before the relevant introduction.</p>
                <p>45.6. Circumvention constitutes a material breach of these Terms.</p>
                <p>45.7. Venduro may recover losses reasonably caused by circumvention, including reasonably demonstrable lost gross margin calculated over the remainder of the restricted period, in addition to other remedies available under these Terms or applicable law.</p>
                <h3 class="text-xl font-semibold text-gray-900">46. Supplier Product Content Licence</h3>
                <p>46.1. The Supplier represents and warrants that it has sufficient rights to provide Venduro with Product information, images and other content supplied by it.</p>
                <p>46.2. The Supplier grants Venduro a worldwide, non-exclusive, royalty-free, transferable and sublicensable licence to:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>use;</li>
                    <li>copy;</li>
                    <li>store;</li>
                    <li>reproduce;</li>
                    <li>publish;</li>
                    <li>translate;</li>
                    <li>edit;</li>
                    <li>adapt;</li>
                    <li>distribute; and</li>
                    <li>communicate</li>
                </ul>
                <p>Supplier-provided Product content for purposes reasonably connected with Venduro’s business.</p>
                <p>46.3. This includes use in:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>Product listings;</li>
                    <li>catalogues;</li>
                    <li>product feeds;</li>
                    <li>consolidated feeds;</li>
                    <li>advertising;</li>
                    <li>Buyer communications;</li>
                    <li>newsletters;</li>
                    <li>sales materials;</li>
                    <li>marketplaces; and</li>
                    <li>Order processing.</li>
                </ul>
                <h3 class="text-xl font-semibold text-gray-900">47. Venduro Intellectual Property and Platform Data</h3>
                <p>47.1. Venduro or its licensors own all rights in the Platform, including:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>software;</li>
                    <li>database structure;</li>
                    <li>design;</li>
                    <li>branding;</li>
                    <li>functionality;</li>
                    <li>proprietary catalogue structure;</li>
                    <li>internal identifiers; and</li>
                    <li>non-Supplier proprietary content.</li>
                </ul>
                <p>47.2. Users receive no ownership rights in the Platform.</p>
                <p>47.3. Users may not without Venduro’s prior permission:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>copy substantial parts of the Platform;</li>
                    <li>systematically scrape Platform data;</li>
                    <li>reverse engineer Platform functionality;</li>
                    <li>circumvent technical restrictions;</li>
                    <li>create competing commercial databases using Venduro data; or</li>
                    <li>commercially redistribute proprietary Platform data,</li>
                </ul>
                <p>except to the extent expressly permitted by Venduro, the applicable Platform functionality, API, product feed, consolidated feed or separate written agreement.</p>
                <p>47.4. Authorised use of a Venduro feed does not grant any ownership rights in Venduro’s Platform, proprietary database structure or other intellectual property.</p>
                <h3 class="text-xl font-semibold text-gray-900">48. Prohibited Conduct</h3>
                <p>Users must not:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>engage in fraud;</li>
                    <li>provide false documents;</li>
                    <li>trade counterfeit or stolen goods;</li>
                    <li>engage in unlawful tax activity;</li>
                    <li>manipulate stock or pricing information;</li>
                    <li>misuse Confidential Information;</li>
                    <li>bypass security;</li>
                    <li>introduce malicious software or code;</li>
                    <li>impersonate another business;</li>
                    <li>violate sanctions;</li>
                    <li>engage in money laundering;</li>
                    <li>offer illegal Products;</li>
                    <li>intentionally misuse the Platform; or</li>
                    <li>use Venduro in a manner reasonably likely to cause material legal, financial or reputational harm.</li>
                </ul>
                <h3 class="text-xl font-semibold text-gray-900">49. Sanctions, AML and Compliance</h3>
                <p>49.1. Users represent that they are not knowingly acting on behalf of a sanctioned or prohibited person in connection with Venduro.</p>
                <p>49.2. Venduro may:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>request additional information;</li>
                    <li>delay processing;</li>
                    <li>temporarily suspend payment;</li>
                    <li>refuse an Order;</li>
                    <li>suspend shipments;</li>
                    <li>restrict functionality; or</li>
                    <li>terminate an account</li>
                </ul>
                <p>where reasonably necessary to comply with sanctions, AML, fraud-prevention or other legal and compliance requirements.</p>
                <p>49.3. Venduro is not required to complete a transaction where Venduro reasonably considers that doing so may violate applicable law or expose Venduro to material compliance risk.</p>
                <h2 class="text-2xl font-semibold text-gray-900 pt-4">PART V – REMEDIES AND LIABILITY</h2>
                <h3 class="text-xl font-semibold text-gray-900">50. Suspension and Termination</h3>
                <p>50.1. Venduro may restrict, suspend or terminate a user account where there is reasonable evidence of:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>material breach of these Terms;</li>
                    <li>repeated fulfilment failure;</li>
                    <li>repeated stock inaccuracies;</li>
                    <li>non-payment;</li>
                    <li>fraud;</li>
                    <li>sanctions concerns;</li>
                    <li>AML concerns;</li>
                    <li>counterfeit Products;</li>
                    <li>intellectual property infringement;</li>
                    <li>Product safety concerns;</li>
                    <li>regulatory non-compliance;</li>
                    <li>misuse of Confidential Information;</li>
                    <li>circumvention;</li>
                    <li>security misuse; or</li>
                    <li>material legal, financial or reputational risk.</li>
                </ul>
                <p>50.2. Venduro may take immediate action without prior notice where reasonably necessary because of:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>fraud;</li>
                    <li>Product safety;</li>
                    <li>sanctions;</li>
                    <li>security;</li>
                    <li>counterfeit concerns;</li>
                    <li>intellectual property claims;</li>
                    <li>regulatory requirements;</li>
                    <li>failure to maintain insurance required under clause 29; or</li>
                    <li>urgent protection of Venduro or third parties.</li>
                </ul>
                <p>50.3. In other cases Venduro may, where reasonably practicable, allow the user an opportunity to remedy the breach.</p>
                <p>50.4. Venduro may remove or restrict individual Products without terminating the entire account.</p>
                <p>50.5. Termination does not affect:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>outstanding payment obligations;</li>
                    <li>existing claims;</li>
                    <li>warranties;</li>
                    <li>indemnities;</li>
                    <li>confidentiality obligations;</li>
                    <li>data obligations;</li>
                    <li>intellectual property rights;</li>
                    <li>non-circumvention obligations;</li>
                    <li>accrued liabilities; or</li>
                    <li>other clauses intended to survive termination.</li>
                </ul>
                <h3 class="text-xl font-semibold text-gray-900">51. Limitation of Venduro Liability</h3>
                <p>51.1. Nothing in these Terms excludes or limits liability for:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>wilful misconduct;</li>
                    <li>gross negligence;</li>
                    <li>death or personal injury where liability may not legally be limited;</li>
                    <li>non-pecuniary damage where liability may not legally be limited;</li>
                    <li>fraud; or</li>
                    <li>any other liability that cannot be excluded or limited under mandatory law.</li>
                </ul>
                <p>51.2. Subject to clause 51.1 and to the fullest extent permitted by applicable law, Venduro is not liable for:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>indirect loss;</li>
                    <li>consequential loss;</li>
                    <li>loss of profit;</li>
                    <li>loss of anticipated profit;</li>
                    <li>loss of revenue;</li>
                    <li>loss of opportunity;</li>
                    <li>loss of anticipated savings;</li>
                    <li>reputational loss;</li>
                    <li>loss caused by third-party marketplace suspension or restriction;</li>
                    <li>loss arising from the Buyer’s own resale decisions; or</li>
                    <li>loss caused by information or conduct attributable to another Platform user, except to the extent that such loss results from Venduro’s own breach of an obligation owed directly by Venduro to the claimant.</li>
                </ul>
                <p>51.3. Subject to clause 51.1, where a claim relates to a specific Order, Venduro’s aggregate liability arising out of or in connection with that Order shall not exceed:</p>
                <p>a. in relation to a Buyer Order, the net purchase price paid or payable to Venduro for the specific Products directly giving rise to the claim; and</p>
                <p>b. in relation to a Purchase Order, the amount paid or payable by Venduro to the Supplier for the specific Products directly giving rise to the claim.</p>
                <p>51.4. Where a claim does not relate to a specific Order, Venduro’s aggregate liability to the relevant user in any 12-month period, in respect of all claims arising in that period taken together, shall not exceed EUR 500 or, if greater, the aggregate fees actually paid directly by that user to Venduro during the preceding 12 months. This cap is inclusive of interest and costs to the maximum extent permitted by applicable law.</p>
                <p>51.5. Multiple claims arising from the same underlying event or substantially related events shall be treated as one claim for the purposes of the applicable liability cap.</p>
                <h3 class="text-xl font-semibold text-gray-900">52. Platform Availability</h3>
                <p>52.1. Venduro does not guarantee that the Platform will be uninterrupted, continuously available or error-free.</p>
                <p>52.2. Venduro may:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>perform maintenance;</li>
                    <li>change functionality;</li>
                    <li>restrict features;</li>
                    <li>suspend integrations;</li>
                    <li>modify feeds; or</li>
                    <li>discontinue functionality.</li>
                </ul>
                <p>52.3. Subject to mandatory law, Venduro is not liable for losses caused solely by temporary Platform unavailability or planned or reasonably necessary maintenance.</p>
                <h3 class="text-xl font-semibold text-gray-900">53. Force Majeure</h3>
                <p>53.1. A party is not liable for failure or delay caused by circumstances genuinely outside its reasonable control where the requirements of applicable law are satisfied.</p>
                <p>53.2. Such circumstances may include:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>war;</li>
                    <li>natural disaster;</li>
                    <li>government prohibition;</li>
                    <li>extraordinary customs closure;</li>
                    <li>widespread transportation disruption;</li>
                    <li>major infrastructure failure;</li>
                    <li>epidemic;</li>
                    <li>civil disorder; or</li>
                    <li>comparable extraordinary events.</li>
                </ul>
                <p>53.3. For the avoidance of doubt, the following do not by themselves constitute Force Majeure:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>lack of funds;</li>
                    <li>ordinary price changes;</li>
                    <li>insufficient stock;</li>
                    <li>inability to purchase Products at an expected price;</li>
                    <li>ordinary labour shortages;</li>
                    <li>ordinary transportation delays;</li>
                    <li>required Products being unavailable on the market; or</li>
                    <li>failure by the affected party’s own contractual counterparty to perform.</li>
                </ul>
                <p>53.4. The affected party must notify the other party without undue delay and take reasonable steps to mitigate the consequences. A party’s payment obligations under these Terms are not excused or suspended by a Force Majeure event affecting that party’s own performance of a non-payment obligation.</p>
                <p>53.5. If a Force Majeure event materially prevents performance of an affected Order for more than 30 days, Venduro may cancel the affected part of the Order, subject to any mandatory liability that cannot legally be excluded.</p>
                <h2 class="text-2xl font-semibold text-gray-900 pt-4">PART VI – MISCELLANEOUS</h2>
                <h3 class="text-xl font-semibold text-gray-900">54. Electronic Communications and Notices</h3>
                <p>54.1. Venduro may communicate with users through:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>email;</li>
                    <li>Seller Portal;</li>
                    <li>Buyer Portal;</li>
                    <li>Platform notifications;</li>
                    <li>invoices; or</li>
                    <li>another electronic method normally used between the parties.</li>
                </ul>
                <p>54.2. Users are responsible for maintaining complete and accurate contact details.</p>
                <p>54.3. Unless a mandatory rule requires another method, an electronic communication sent to the user’s registered email address or account is deemed delivered when transmitted, provided Venduro has not received an automated notice of permanent delivery failure.</p>
                <p>54.4. Electronic Orders, confirmations, invoices and communications may be retained and relied upon as evidence.</p>
                <h3 class="text-xl font-semibold text-gray-900">55. Changes to Terms</h3>
                <p>55.1. Venduro may amend these Terms from time to time.</p>
                <p>55.2. Material amendments will normally be communicated at least 7 days before taking effect.</p>
                <p>55.3. Venduro may implement amendments with a shorter notice period or immediately where reasonably required because of:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>applicable law;</li>
                    <li>regulatory requirements;</li>
                    <li>Product safety;</li>
                    <li>sanctions;</li>
                    <li>fraud prevention;</li>
                    <li>security; or</li>
                    <li>another urgent compliance requirement.</li>
                </ul>
                <p>55.4. Continued use of the Platform, submission of a Buyer Order or acceptance of a Purchase Order after the effective date of amended Terms constitutes acceptance of those amended Terms, whether or not the user has actually reviewed the amended Terms.</p>
                <p>55.5. Unless required by law or expressly agreed, amendments do not retrospectively alter the agreed commercial terms of an Order already accepted.</p>
                <h3 class="text-xl font-semibold text-gray-900">56. Assignment</h3>
                <p>56.1. Venduro may assign, transfer, charge or otherwise deal with its rights and receivables under these Terms to:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>an affiliated company;</li>
                    <li>financing provider;</li>
                    <li>insurer;</li>
                    <li>successor;</li>
                    <li>purchaser of Venduro’s business; or</li>
                    <li>another third party in connection with legitimate financing, insurance or corporate transactions.</li>
                </ul>
                <p>56.2. Venduro may transfer its contractual obligations to an affiliate, successor or purchaser of the relevant business to the extent permitted by applicable law.</p>
                <p>56.3. A user may not assign or transfer its rights or obligations without Venduro’s prior written consent.</p>
                <h3 class="text-xl font-semibold text-gray-900">57. No Partnership or Agency</h3>
                <p>Nothing in these Terms creates:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>a partnership;</li>
                    <li>joint venture;</li>
                    <li>employment relationship;</li>
                    <li>fiduciary relationship; or</li>
                    <li>authority for a Supplier or Buyer to bind Venduro.</li>
                </ul>
                <h3 class="text-xl font-semibold text-gray-900">58. No Waiver</h3>
                <p>58.1. Failure or delay in exercising a contractual or legal right does not constitute waiver of that right.</p>
                <p>58.2. A waiver is effective only in relation to the specific matter for which it is given.</p>
                <h3 class="text-xl font-semibold text-gray-900">59. Severability</h3>
                <p>59.1. If any provision of these Terms is determined to be invalid, illegal or unenforceable, the remaining provisions remain effective.</p>
                <p>59.2. Where legally possible, the invalid or unenforceable provision shall be interpreted or replaced in a manner that most closely reflects its legitimate commercial purpose and, to the extent possible, preserves the economic benefit intended for Venduro under the original provision.</p>
                <h3 class="text-xl font-semibold text-gray-900">60. Entire Agreement</h3>
                <p>60.1. These Terms, together with applicable documents identified in clause 5, constitute the agreement relating to the relevant use of Venduro and applicable Orders.</p>
                <p>60.2. The parties acknowledge that they do not rely upon statements not incorporated into the applicable contractual documents, except in cases of fraud or where such exclusion is prohibited by mandatory law.</p>
                <h3 class="text-xl font-semibold text-gray-900">61. Survival</h3>
                <p>Clauses relating to:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>payment;</li>
                    <li>Product warranties;</li>
                    <li>claims;</li>
                    <li>indemnities;</li>
                    <li>confidentiality;</li>
                    <li>personal data;</li>
                    <li>intellectual property;</li>
                    <li>non-circumvention;</li>
                    <li>liability;</li>
                    <li>governing law; and</li>
                    <li>dispute resolution</li>
                </ul>
                <p>survive termination to the extent necessary to give them effect.</p>
                <h3 class="text-xl font-semibold text-gray-900">62. Language</h3>
                <p>62.1. These Terms may be made available in more than one language.</p>
                <p>62.2. Unless mandatory law provides otherwise, the English-language version prevails in the event of inconsistency between translations.</p>
                <h3 class="text-xl font-semibold text-gray-900">63. Governing Law</h3>
                <p>These Terms and all contractual and non-contractual obligations arising out of or in connection with them are governed by the laws of the Republic of Lithuania.</p>
                <p>The United Nations Convention on Contracts for the International Sale of Goods (CISG) is excluded to the fullest extent permitted by law.</p>
                <h3 class="text-xl font-semibold text-gray-900">64. Dispute Resolution and Jurisdiction</h3>
                <p>64.1. The parties should first attempt in good faith to resolve disputes commercially.</p>
                <p>64.2. If a dispute cannot be resolved, the courts of the Republic of Lithuania having territorial jurisdiction over Venduro’s registered office shall have exclusive jurisdiction to the extent permitted by applicable law.</p>
                <p>64.3. Venduro may seek:</p>
                <ul class="list-disc pl-6 space-y-2">
                    <li>interim measures;</li>
                    <li>protective measures;</li>
                    <li>injunctions;</li>
                    <li>asset-preservation measures;</li>
                    <li>debt-enforcement measures; or</li>
                    <li>other urgent protective relief</li>
                </ul>
                <p>in any other jurisdiction where legally permitted and reasonably necessary to protect Venduro’s rights or assets.</p>
                <h3 class="text-xl font-semibold text-gray-900">65. Contact</h3>
                <p>
                    UAB Venduro<br />
                    Partizanų g. 14-206<br />
                    LT-50211 Kaunas<br />
                    Lithuania
                </p>
                <p>Company code: 307175838<br />VAT number: LT100018086112</p>
                <p>
                    Email:
                    <a href="mailto:support@venduro.io" class="text-primary-600 hover:text-primary-800">support@venduro.io</a>
                </p>
            </div>
        </div>
    `,
})
export class TermsConditionsComponent {}
